import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

const cp=x=>x==null?x:JSON.parse(JSON.stringify(x));

function create(opts={}){
  const engines=opts.engines||{};
  const journal=opts.journal||null;
  const persist=opts.persist||null;

  async function run(input={}){
    const ctx={input:cp(input),steps:[],errors:[],warnings:[]};

    function step(name,fn,required=false){
      try{
        if(typeof fn!=='function'){
          if(required) ctx.errors.push({step:name,reason:'MISSING_ENGINE'});
          else ctx.warnings.push({step:name,reason:'ENGINE_NOT_AVAILABLE'});
          return null;
        }
        const out=fn(cp(ctx));
        ctx.steps.push({name,status:'OK',output:cp(out)});
        return out;
      }catch(e){
        ctx.steps.push({name,status:'ERROR',error:String(e&&e.message||e)});
        ctx.errors.push({step:name,reason:String(e&&e.message||e)});
        return null;
      }
    }

    const snapshot=step('snapshot',engines.snapshot,true);
    const meaningful=step('meaningfulChange',engines.meaningfulChange,false);
    const thesisDrift=step('thesisDrift',engines.thesisDrift,false);
    const invalidation=step('invalidation',engines.invalidation,false);
    const reopen=step('reopen',engines.reopen,false);
    const integrity=step('integrity',engines.integrity,true);
    const integrityGate=step('integrityGate',engines.integrityGate,true);
    const priority=step('priority',engines.priority,false);
    const brief=step('brief',engines.brief,true);

    const blocked=ctx.errors.length>0 || !!(integrityGate && (
      integrityGate.status==='BLOCK' ||
      integrityGate.state==='BLOCK' ||
      integrityGate.readiness==='BLOCKED' ||
      integrityGate.decision==='BLOCK'
    ));

    const result={
      version:VERSION,
      symbol:input.symbol||null,
      scope:input.scope||'RESEARCH',
      status:blocked?'REVIEW':'READY',
      snapshot:cp(snapshot),
      meaningfulChange:cp(meaningful),
      thesisDrift:cp(thesisDrift),
      invalidation:cp(invalidation),
      reopen:cp(reopen),
      integrity:cp(integrity),
      integrityGate:cp(integrityGate),
      priority:cp(priority),
      brief:cp(brief),
      errors:cp(ctx.errors),
      warnings:cp(ctx.warnings)
    };

    if(journal && typeof journal.append==='function' && result.symbol){
      journal.append({
        symbol:result.symbol,
        scope:result.scope,
        eventType:'PIPELINE_COMPLETED',
        reason:result.status,
        evidenceFingerprint:input.evidenceFingerprint||null,
        thesisFingerprint:input.thesisFingerprint||null,
        payload:{
          status:result.status,
          errorCount:result.errors.length,
          warningCount:result.warnings.length
        }
      });
    }

    if(persist && typeof persist.save==='function' && result.symbol){
      try{
        await persist.save({
          symbol:result.symbol,
          scope:result.scope,
          evidenceFingerprint:input.evidenceFingerprint||null,
          thesisFingerprint:input.thesisFingerprint||null,
          snapshot:result.snapshot,
          readiness:result.integrityGate,
          integrity:result.integrity,
          brief:result.brief,
          openGaps:cp(input.openGaps||[]),
          invalidations:cp(result.invalidation||[]),
          completionMemory:cp(input.completionMemory||[]),
          metadata:{pipelineStatus:result.status}
        });
      }catch(e){
        result.status='REVIEW';
        result.errors.push({step:'persist',reason:String(e&&e.message||e)});
      }
    }

    return Object.freeze(result);
  }

  return Object.freeze({VERSION,run});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const calls=[];
  const mk=n=>(ctx)=>{calls.push(n);return {engine:n,ok:true};};
  const journalEvents=[];
  const persisted=[];

  const p=create({
    engines:{
      snapshot:mk('snapshot'),
      meaningfulChange:mk('meaningfulChange'),
      thesisDrift:mk('thesisDrift'),
      invalidation:mk('invalidation'),
      reopen:mk('reopen'),
      integrity:mk('integrity'),
      integrityGate:()=>({state:'READY'}),
      priority:mk('priority'),
      brief:mk('brief')
    },
    journal:{append:e=>journalEvents.push(e)},
    persist:{save:async r=>persisted.push(r)}
  });

  const r=await p.run({symbol:'AAA',scope:'OWNED',evidenceFingerprint:'e1',thesisFingerprint:'t1'});
  ok('pipeline returns READY',r.status==='READY');
  ok('required snapshot ran',calls.includes('snapshot'));
  ok('integrity ran',calls.includes('integrity'));
  ok('brief ran',calls.includes('brief'));
  ok('journal event emitted',journalEvents.length===1&&journalEvents[0].eventType==='PIPELINE_COMPLETED');
  ok('state persisted',persisted.length===1&&persisted[0].symbol==='AAA');
  ok('fingerprints persisted',persisted[0].evidenceFingerprint==='e1'&&persisted[0].thesisFingerprint==='t1');

  const blocked=create({
    engines:{
      snapshot:()=>({}),
      integrity:()=>({state:'BROKEN'}),
      integrityGate:()=>({state:'BLOCK'}),
      brief:()=>({})
    }
  });
  const b=await blocked.run({symbol:'BBB'});
  ok('BLOCK gate forces REVIEW',b.status==='REVIEW');

  const missing=create({engines:{snapshot:()=>({}),integrity:()=>({}),brief:()=>({})}});
  const m=await missing.run({symbol:'CCC'});
  ok('missing required integrity gate causes REVIEW',m.status==='REVIEW');
  ok('missing required engine recorded',m.errors.some(x=>x.step==='integrityGate'&&x.reason==='MISSING_ENGINE'));

  const optional=create({
    engines:{snapshot:()=>({}),integrity:()=>({}),integrityGate:()=>({state:'READY'}),brief:()=>({})}
  });
  const o=await optional.run({symbol:'DDD'});
  ok('missing optional engines only warn',o.status==='READY'&&o.warnings.length>0);

  const failing=create({
    engines:{
      snapshot:()=>{throw Error('SNAP_FAIL')},
      integrity:()=>({}),
      integrityGate:()=>({state:'READY'}),
      brief:()=>({})
    }
  });
  const f=await failing.run({symbol:'EEE'});
  ok('required engine exception forces REVIEW',f.status==='REVIEW');
  ok('exception preserved',f.errors.some(x=>x.reason==='SNAP_FAIL'));

  const persistFail=create({
    engines:{snapshot:()=>({}),integrity:()=>({}),integrityGate:()=>({state:'READY'}),brief:()=>({})},
    persist:{save:async()=>{throw Error('DB_FAIL')}}
  });
  const pf=await persistFail.run({symbol:'FFF'});
  ok('persist failure forces REVIEW',pf.status==='REVIEW');
  ok('persist error recorded',pf.errors.some(x=>x.step==='persist'&&x.reason==='DB_FAIL'));

  const api=create({engines:{}});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestResearchRuntimePipelineOrchestrator=Object.freeze({VERSION,create,selfTest});
})(engineContext);